import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Typography,
  Space,
  Divider,
  Select,
  InputNumber,
  Tag,
  Modal,
  Alert,
  Skeleton,
  App,
  Card,
  List,
  Progress,
  Radio,
  Descriptions,
  Spin,
  Switch,
} from 'antd';
import {
  SaveOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  LinkOutlined,
  SyncOutlined,
  QrcodeOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import CertificadosManager from './CertificadosManager';
import {
  useGetFacturacionConfigQuery,
  useActivateFacturacionMutation,
  useUpdateFacturacionConfigMutation,
  useDeactivateFacturacionMutation,
  useGetRegimenesFiscalesQuery,
  useGetUsosCFDIQuery,
  useGetFormasPagoQuery,
  useGetMetodosPagoQuery,
  useGetTimbresCompartidosQuery,
  useLazyBuscarPorRfcQuery,
  useVincularPorRfcMutation,
  useResyncTimbresMutation,
  useToggleAutofacturaMutation,
} from '../../services/facturacionApi';
import {
  useGetSettingQuery,
  useGetBoolSettingQuery,
  useUpdateSettingMutation,
} from '../../services/settingsApi';
import type { OrganizacionByRfc } from '../../types/facturacion';

const { Title, Text } = Typography;

const ConfiguracionFiscalTab: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [facturacionForm] = Form.useForm();
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  // Estado para modo de activación: 'new' o 'link'
  const [activationMode, setActivationMode] = useState<'new' | 'link'>('new');
  const [rfcBusqueda, setRfcBusqueda] = useState('');
  const [organizacionEncontrada, setOrganizacionEncontrada] = useState<OrganizacionByRfc | null>(null);
  // Serie y folio para vinculación (cada restaurante debe tener serie única)
  const [serieVinculacion, setSerieVinculacion] = useState('');
  const [folioInicialVinculacion, setFolioInicialVinculacion] = useState<number | null>(1);

  // Facturacion API hooks
  const { data: facturacionConfig, isLoading: loadingFacturacion } = useGetFacturacionConfigQuery();
  const [activateFacturacion, { isLoading: activating }] = useActivateFacturacionMutation();
  const [updateFacturacionConfig, { isLoading: updatingFacturacion }] = useUpdateFacturacionConfigMutation();
  const [deactivateFacturacion, { isLoading: deactivating }] = useDeactivateFacturacionMutation();

  // Catalogos SAT
  const { data: regimenesFiscales = [] } = useGetRegimenesFiscalesQuery();
  const { data: usosCFDI = [] } = useGetUsosCFDIQuery();
  const { data: formasPago = [] } = useGetFormasPagoQuery();
  const { data: metodosPago = [] } = useGetMetodosPagoQuery();

  // Búsqueda y vinculación por RFC
  const [buscarPorRfc, { isLoading: buscandoRfc }] = useLazyBuscarPorRfcQuery();
  const [vincularPorRfc, { isLoading: vinculando }] = useVincularPorRfcMutation();

  // Timbres Compartidos - only fetch when facturacion is enabled
  const { data: timbresCompartidos, refetch: refetchTimbres } = useGetTimbresCompartidosQuery(undefined, {
    skip: !facturacionConfig?.facturacionEnabled,
  });
  const [resyncTimbres, { isLoading: resyncingTimbres }] = useResyncTimbresMutation();
  const [toggleAutofactura, { isLoading: togglingAutofactura }] = useToggleAutofacturaMutation();

  // Setting for unmapped products description - only fetch when facturacion is enabled
  const { data: descripcionNoMapeadosSetting } = useGetSettingQuery('DescripcionProductosNoMapeados', {
    skip: !facturacionConfig?.facturacionEnabled,
  });
  const [updateSetting] = useUpdateSettingMutation();
  const [descripcionNoMapeados, setDescripcionNoMapeados] = useState<string>('');

  // Setting: print autofactura QR on precuenta tickets (default true)
  const { data: qrPrecuentaSetting } = useGetBoolSettingQuery('AutofacturaQr_Precuenta', {
    skip: !facturacionConfig?.autofacturaEnabled,
  });
  const qrEnPrecuenta = qrPrecuentaSetting?.value ?? true;
  const [togglingQrPrecuenta, setTogglingQrPrecuenta] = useState(false);

  const handleToggleQrPrecuenta = async (checked: boolean) => {
    setTogglingQrPrecuenta(true);
    try {
      await updateSetting({ key: 'AutofacturaQr_Precuenta', value: String(checked) }).unwrap();
      messageApi.success(checked
        ? 'El QR se imprimirá en el ticket de precuenta'
        : 'El QR ya no se imprimirá en el ticket de precuenta');
    } catch (error: any) {
      console.error('Error updating AutofacturaQr_Precuenta setting:', error);
      messageApi.error(error?.data?.message || 'Error al actualizar la configuración');
    } finally {
      setTogglingQrPrecuenta(false);
    }
  };

  // Load facturacion config into form
  useEffect(() => {
    if (facturacionConfig) {
      facturacionForm.setFieldsValue({
        rfc: facturacionConfig.rfc,
        razonSocial: facturacionConfig.razonSocial,
        regimenFiscal: facturacionConfig.regimenFiscal,
        codigoPostal: facturacionConfig.codigoPostal,
        serie: facturacionConfig.serie,
        folioInicial: facturacionConfig.folioInicial,
        usoCFDIDefault: facturacionConfig.usoCFDIDefault,
        formaPagoDefault: facturacionConfig.formaPagoDefault,
        metodoPagoDefault: facturacionConfig.metodoPagoDefault,
      });
    }
  }, [facturacionConfig, facturacionForm]);

  // Load setting for unmapped products description
  useEffect(() => {
    if (descripcionNoMapeadosSetting?.value) {
      setDescripcionNoMapeados(descripcionNoMapeadosSetting.value);
    }
  }, [descripcionNoMapeadosSetting]);

  const handleActivateFacturacion = async () => {
    try {
      const values = await facturacionForm.validateFields([
        'rfc',
        'razonSocial',
        'regimenFiscal',
        'codigoPostal',
        'serie',
        'folioInicial',
      ]);

      const result = await activateFacturacion(values).unwrap();

      if (result.success) {
        messageApi.success(result.message || 'Facturación activada correctamente', 5);
        setTimeout(() => setShowActivateModal(false), 500);
      } else {
        messageApi.error(result.message || 'Error al activar facturación', 5);
        setTimeout(() => setShowActivateModal(false), 500);
      }
    } catch (error: any) {
      console.error('Error activating facturacion:', error);
      messageApi.error(error?.data?.message || 'Error al activar facturación', 5);
      setTimeout(() => setShowActivateModal(false), 500);
    }
  };

  const handleUpdateFacturacion = async () => {
    try {
      const values = await facturacionForm.validateFields();
      await updateFacturacionConfig(values).unwrap();

      // Also save the unmapped products description setting
      if (descripcionNoMapeados.trim()) {
        await updateSetting({
          key: 'DescripcionProductosNoMapeados',
          value: descripcionNoMapeados.trim(),
        }).unwrap();
      }

      messageApi.success('Configuración de facturación actualizada correctamente');
    } catch (error: any) {
      console.error('Error updating facturacion config:', error);
      messageApi.error(error?.data?.message || 'Error al actualizar configuración de facturación');
    }
  };

  // Búsqueda de organización existente por RFC
  const handleBuscarRfc = async () => {
    if (!rfcBusqueda || rfcBusqueda.length < 12) {
      messageApi.warning('Ingrese un RFC válido (12-13 caracteres)');
      return;
    }

    try {
      const result = await buscarPorRfc(rfcBusqueda.toUpperCase()).unwrap();
      setOrganizacionEncontrada(result);
      messageApi.success('Organización encontrada');
    } catch (error: any) {
      setOrganizacionEncontrada(null);
      if (error?.status === 404) {
        messageApi.warning('No se encontró ninguna organización con ese RFC. Use la opción de configuración nueva.');
      } else {
        messageApi.error(error?.data?.message || 'Error al buscar organización');
      }
    }
  };

  // Vinculación a organización existente
  const handleVincularPorRfc = async () => {
    if (!organizacionEncontrada) {
      messageApi.error('Primero busque una organización por RFC');
      return;
    }

    if (!serieVinculacion || serieVinculacion.trim() === '') {
      messageApi.warning('La serie es obligatoria para identificar las facturas de este restaurante');
      return;
    }

    try {
      const result = await vincularPorRfc({
        rfc: organizacionEncontrada.rfc,
        serie: serieVinculacion.trim().toUpperCase(),
        folioInicial: folioInicialVinculacion || 1,
      }).unwrap();

      if (result.success) {
        messageApi.success(result.message || 'Vinculación exitosa', 5);
        // Limpiar estados
        setOrganizacionEncontrada(null);
        setRfcBusqueda('');
        setSerieVinculacion('');
        setFolioInicialVinculacion(1);
        setActivationMode('new');
      } else {
        messageApi.error(result.message || 'Error al vincular');
      }
    } catch (error: any) {
      console.error('Error vinculando por RFC:', error);
      messageApi.error(error?.data?.message || 'Error al vincular con la organización');
    }
  };

  const handleDeactivateFacturacion = () => {
    setShowDeactivateModal(true);
  };

  const confirmDeactivateFacturacion = async () => {
    try {
      await deactivateFacturacion().unwrap();
      messageApi.success('Facturación desactivada correctamente', 5);
      setTimeout(() => setShowDeactivateModal(false), 500);
    } catch (error: any) {
      console.error('Error deactivating facturacion:', error);
      messageApi.error(error?.data?.message || 'Error al desactivar facturación', 5);
      setTimeout(() => setShowDeactivateModal(false), 500);
    }
  };

  // Resync timbres contribution from Stripe subscription
  const handleResyncTimbres = async () => {
    try {
      await resyncTimbres().unwrap();
      messageApi.success('Timbres resincronizados correctamente');
      refetchTimbres();
    } catch (error: any) {
      console.error('Error resyncing timbres:', error);
      messageApi.error(error?.data?.message || 'Error al resincronizar timbres');
    }
  };

  const handleToggleAutofactura = async (enabled: boolean) => {
    try {
      await toggleAutofactura({ enabled }).unwrap();
      messageApi.success(enabled ? 'Autofacturación activada' : 'Autofacturación desactivada');
    } catch (error: any) {
      console.error('Error toggling autofactura:', error);
      messageApi.error(error?.data?.message || 'Error al cambiar autofacturación');
    }
  };

  const autofacturaUrl = facturacionConfig?.autofacturaCode
    ? `${window.location.origin}/autofactura/${facturacionConfig.autofacturaCode}`
    : null;

  const handleCopyAutofacturaUrl = () => {
    if (autofacturaUrl) {
      navigator.clipboard.writeText(autofacturaUrl);
      messageApi.success('URL copiada al portapapeles');
    }
  };

  if (loadingFacturacion) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  return (
    <div>
      {/* Status Badge */}
      <div style={{ marginBottom: 16 }}>
        {facturacionConfig?.facturacionEnabled ? (
          <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
            Facturación Activa
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default" style={{ fontSize: 14, padding: '4px 12px' }}>
            Facturación Inactiva
          </Tag>
        )}
        {facturacionConfig?.activatedAt && (
          <Text type="secondary" style={{ marginLeft: 12 }}>
            Activada el {formatInTimeZone(facturacionConfig.activatedAt, 'DD/MM/YYYY')}
          </Text>
        )}
      </div>

      {/* Selector de modo de activación - Solo mostrar cuando facturación NO está activa */}
      {!facturacionConfig?.facturacionEnabled && (
        <Card style={{ marginBottom: 24 }}>
          <Radio.Group
            value={activationMode}
            onChange={(e) => {
              setActivationMode(e.target.value);
              setOrganizacionEncontrada(null);
              setRfcBusqueda('');
              setSerieVinculacion('');
              setFolioInicialVinculacion(1);
            }}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value="new">
                <Text strong>Configurar nueva cuenta de facturación</Text>
                <br />
                <Text type="secondary" style={{ marginLeft: 24 }}>
                  Ingrese los datos fiscales para crear una nueva configuración
                </Text>
              </Radio>
              <Radio value="link">
                <Text strong>Ya tengo otra cuenta con facturación activa</Text>
                <br />
                <Text type="secondary" style={{ marginLeft: 24 }}>
                  Vincular a una organización existente usando el mismo RFC
                </Text>
              </Radio>
            </Space>
          </Radio.Group>
        </Card>
      )}

      {/* Modo: Vincular a existente */}
      {!facturacionConfig?.facturacionEnabled && activationMode === 'link' && (
        <Card title="Buscar Organización por RFC" style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              message="Vinculación por RFC"
              description="Si ya tienes otra cuenta en Massrestaurant con facturación activa para el mismo RFC, puedes vincular este restaurante a esa configuración. Los timbres se compartirán entre todas las cuentas."
              type="info"
              showIcon
              icon={<LinkOutlined />}
            />

            <Space.Compact style={{ width: '100%', maxWidth: 400 }}>
              <Input
                placeholder="Ingrese el RFC (ej: XAXX010101000)"
                value={rfcBusqueda}
                onChange={(e) => setRfcBusqueda(e.target.value.toUpperCase())}
                maxLength={13}
                style={{ textTransform: 'uppercase' }}
                onPressEnter={handleBuscarRfc}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleBuscarRfc}
                loading={buscandoRfc}
              >
                Buscar
              </Button>
            </Space.Compact>

            {buscandoRfc && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <Spin tip="Buscando organización..." />
              </div>
            )}

            {organizacionEncontrada && (
              <Card
                type="inner"
                title={
                  <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span>Organización Encontrada</span>
                  </Space>
                }
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="RFC">
                    <Tag color="blue">{organizacionEncontrada.rfc}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Razón Social">
                    {organizacionEncontrada.razonSocial}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nombre">
                    {organizacionEncontrada.nombre}
                  </Descriptions.Item>
                  {organizacionEncontrada.regimenFiscal && (
                    <Descriptions.Item label="Régimen Fiscal">
                      {organizacionEncontrada.regimenFiscal}
                    </Descriptions.Item>
                  )}
                  {organizacionEncontrada.codigoPostal && (
                    <Descriptions.Item label="Código Postal">
                      {organizacionEncontrada.codigoPostal}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Cuentas Vinculadas">
                    <Tag icon={<TeamOutlined />} color="purple">
                      {organizacionEncontrada.contribuyentesActivos} cuenta(s) activa(s)
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Alert
                  message="Serie de Facturas"
                  description="Cada restaurante debe tener una serie única para identificar el origen de sus facturas. Por ejemplo: 'R1', 'SUC2', 'PLAYA'."
                  type="warning"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  style={{ marginBottom: 16 }}
                />

                <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%', marginBottom: 16 }} size="middle">
                  <div style={{ flex: 1, minWidth: isMobile ? '100%' : 150 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Serie *</Text>
                    <Input
                      placeholder="Ej: R1, SUC2, PLAYA"
                      value={serieVinculacion}
                      onChange={(e) => setSerieVinculacion(e.target.value.toUpperCase())}
                      maxLength={10}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: isMobile ? '100%' : 150 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Folio Inicial</Text>
                    <InputNumber
                      placeholder="1"
                      value={folioInicialVinculacion}
                      onChange={(value) => setFolioInicialVinculacion(value)}
                      min={1}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Space>

                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={handleVincularPorRfc}
                  loading={vinculando}
                  size="large"
                  block
                  disabled={!serieVinculacion.trim()}
                >
                  Vincular a esta Organización
                </Button>
              </Card>
            )}
          </Space>
        </Card>
      )}

      {/* Formulario completo - Solo mostrar cuando es modo 'new' O cuando ya está activa */}
      {(facturacionConfig?.facturacionEnabled || activationMode === 'new') && (
        <Form form={facturacionForm} layout="vertical">
          {/* Datos Fiscales del Emisor */}
          <Title level={5} style={{ marginTop: 16 }}>Datos Fiscales del Emisor</Title>

          <Form.Item
            name="rfc"
            label="RFC"
            rules={[
              { required: true, message: 'Ingrese el RFC' },
              { pattern: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/, message: 'RFC inválido' },
            ]}
          >
            <Input
              placeholder="XAXX010101000"
              maxLength={13}
              disabled={facturacionConfig?.facturacionEnabled}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item
            name="razonSocial"
            label="Razón Social"
            rules={[{ required: true, message: 'Ingrese la razón social' }]}
          >
            <Input
              placeholder="Nombre o razón social del emisor"
              disabled={facturacionConfig?.facturacionEnabled}
            />
          </Form.Item>

          <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
            <Form.Item
              name="regimenFiscal"
              label="Régimen Fiscal"
              rules={[{ required: true, message: 'Seleccione el régimen fiscal' }]}
              style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}
            >
              <Select
                placeholder="Seleccione régimen fiscal"
                disabled={facturacionConfig?.facturacionEnabled}
                showSearch
                optionFilterProp="children"
              >
                {regimenesFiscales.map((regimen) => (
                  <Select.Option key={regimen.clave} value={regimen.clave}>
                    {regimen.clave} - {regimen.descripcion}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="codigoPostal"
              label="Código Postal"
              rules={[
                { required: true, message: 'Ingrese el código postal' },
                { pattern: /^\d{5}$/, message: 'Debe ser un código postal de 5 dígitos' },
              ]}
              style={{ flex: 1, minWidth: isMobile ? '100%' : 150 }}
            >
              <Input
                placeholder="01000"
                maxLength={5}
                disabled={facturacionConfig?.facturacionEnabled}
              />
            </Form.Item>
          </Space>

          {/* Configuración de Facturación */}
          <Title level={5} style={{ marginTop: 24 }}>Configuración de Emisión</Title>

          <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
            <Form.Item
              name="serie"
              label="Serie de Facturas"
              style={{ flex: 1, minWidth: isMobile ? '100%' : 150 }}
            >
              <Input placeholder="A, B, FAC..." maxLength={10} />
            </Form.Item>

            {/* Ocultar Folio Inicial para cuentas secundarias - el folio es compartido */}
            {!timbresCompartidos?.esCuentaSecundaria && (
              <Form.Item
                name="folioInicial"
                label="Folio Inicial"
                style={{ flex: 1, minWidth: isMobile ? '100%' : 150 }}
              >
                <InputNumber placeholder="1" min={1} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Space>

          {/* Valores por Defecto */}
          <Title level={5} style={{ marginTop: 24 }}>Valores por Defecto</Title>

          <Form.Item name="usoCFDIDefault" label="Uso CFDI por Defecto">
            <Select
              placeholder="Seleccione uso CFDI"
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {usosCFDI.map((uso) => (
                <Select.Option key={uso.clave} value={uso.clave}>
                  {uso.clave} - {uso.descripcion}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
            <Form.Item
              name="formaPagoDefault"
              label="Forma de Pago por Defecto"
              style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}
            >
              <Select
                placeholder="Seleccione forma de pago"
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {formasPago.map((forma) => (
                  <Select.Option key={forma.clave} value={forma.clave}>
                    {forma.clave} - {forma.descripcion}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="metodoPagoDefault"
              label="Método de Pago por Defecto"
              style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}
            >
              <Select
                placeholder="Seleccione método de pago"
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {metodosPago.map((metodo) => (
                  <Select.Option key={metodo.clave} value={metodo.clave}>
                    {metodo.clave} - {metodo.descripcion}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Space>

          {/* Descripción para productos no mapeados */}
          <Form.Item
            label="Descripción para productos no mapeados"
            extra="Texto que aparecerá en facturas para productos sin clasificación SAT"
            style={{ marginTop: 16 }}
          >
            <Input
              value={descripcionNoMapeados}
              onChange={(e) => setDescripcionNoMapeados(e.target.value)}
              placeholder="CONSUMO DE ALIMENTOS"
              maxLength={200}
            />
          </Form.Item>

          <Divider />

          {/* Action Buttons */}
          <Space size="middle">
            {!facturacionConfig?.facturacionEnabled ? (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => setShowActivateModal(true)}
                size="large"
              >
                Activar Facturación
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleUpdateFacturacion}
                  loading={updatingFacturacion}
                  size="large"
                >
                  Guardar Cambios
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleDeactivateFacturacion}
                  loading={deactivating}
                  size="large"
                >
                  Desactivar
                </Button>
              </>
            )}
          </Space>
        </Form>
      )}

      {(!facturacionConfig || !facturacionConfig?.facturacionEnabled) && activationMode === 'new' && (
        <Alert
          message="Facturación No Configurada"
          description="Para habilitar la facturación electrónica, complete los datos fiscales y active el servicio."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {/* Certificados Manager - Show when facturacion is enabled AND not a secondary account */}
      {/* Secondary accounts use the certificates from the primary account */}
      {facturacionConfig?.facturacionEnabled && !timbresCompartidos?.esCuentaSecundaria && (
        <CertificadosManager />
      )}

      {/* Alert for secondary accounts about certificates */}
      {facturacionConfig?.facturacionEnabled && timbresCompartidos?.esCuentaSecundaria && (
        <Alert
          message="Certificados Compartidos"
          description="Los certificados digitales (CSD) se administran desde la cuenta principal. Esta cuenta utiliza los mismos certificados automáticamente."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginTop: 24 }}
        />
      )}

      {/* Autofacturacion Toggle - Show when facturacion is enabled */}
      {facturacionConfig?.facturacionEnabled && (
        <Card
          title={
            <Space>
              <QrcodeOutlined style={{ color: 'var(--color-primary)' }} />
              <span>Autofacturación</span>
            </Space>
          }
          style={{ marginTop: 24 }}
          size="small"
        >
          <div style={{ marginBottom: 16 }}>
            <Space align="center">
              <Switch
                checked={facturacionConfig.autofacturaEnabled}
                onChange={handleToggleAutofactura}
                loading={togglingAutofactura}
              />
              <Text>
                {facturacionConfig.autofacturaEnabled
                  ? 'Portal de autofacturación activo'
                  : 'Portal de autofacturación desactivado'}
              </Text>
            </Space>
          </div>

          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Permite a tus comensales generar su propia factura escaneando un QR en su ticket de pago
            o ingresando su código de restaurante en el portal web.
          </Text>

          {facturacionConfig.autofacturaEnabled && (
            <div style={{ marginBottom: 16 }}>
              <Space align="center">
                <Switch
                  checked={qrEnPrecuenta}
                  onChange={handleToggleQrPrecuenta}
                  loading={togglingQrPrecuenta}
                  size="small"
                />
                <Text>Imprimir QR en el ticket de precuenta</Text>
              </Space>
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                El ticket de pago siempre incluye el QR; aquí decides si también aparece en la precuenta.
              </Text>
            </div>
          )}

          {facturacionConfig.autofacturaEnabled && autofacturaUrl && (
            <div style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 8 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                URL del portal:
              </Text>
              <Space>
                <Input
                  value={autofacturaUrl}
                  readOnly
                  style={{ width: isMobile ? '100%' : 400 }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyAutofacturaUrl}
                >
                  Copiar
                </Button>
              </Space>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  Código: <Text code>{facturacionConfig.autofacturaCode}</Text>
                </Text>
              </div>
              <Alert
                message="El QR aparecerá automáticamente en los tickets de pago impresos"
                type="info"
                showIcon
                style={{ marginTop: 12 }}
              />
            </div>
          )}
        </Card>
      )}

      {/* Timbres Compartidos - Show when there are shared timbres */}
      {facturacionConfig?.facturacionEnabled && timbresCompartidos?.esCompartido && (
        <Card
          title={
            <Space>
              <TeamOutlined style={{ color: '#1890ff' }} />
              <span>Timbres Compartidos</span>
            </Space>
          }
          style={{ marginTop: 24 }}
          size="small"
        >
          <Alert
            message="Este RFC comparte timbres con otras cuentas"
            description="Los timbres de tu plan se suman con los de otras organizaciones que usan el mismo RFC."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          <div style={{ marginBottom: 16 }}>
            <Text strong>Resumen de Timbres del Mes:</Text>
            <Progress
              percent={timbresCompartidos.totalTimbresPlan > 0
                ? Math.round((timbresCompartidos.timbresPlanConsumidos / timbresCompartidos.totalTimbresPlan) * 100)
                : 0
              }
              status={
                timbresCompartidos.timbresPlanDisponibles < 10
                  ? 'exception'
                  : 'active'
              }
              format={() =>
                `${timbresCompartidos.timbresPlanDisponibles} disponibles de ${timbresCompartidos.totalTimbresPlan}`
              }
              style={{ marginTop: 8 }}
            />
          </div>

          {/* Resync button - useful when timbres don't reflect current Stripe plan */}
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              type="text"
              size="small"
              icon={<SyncOutlined spin={resyncingTimbres} />}
              onClick={handleResyncTimbres}
              loading={resyncingTimbres}
            >
              Resincronizar timbres
            </Button>
          </div>

          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary" style={{ fontSize: 12 }}>Contribuyentes al Pool</Text>
          </Divider>

          <List
            size="small"
            dataSource={timbresCompartidos.contribuyentes.filter(c => c.activo)}
            renderItem={(contribuyente) => (
              <List.Item>
                <List.Item.Meta
                  title={contribuyente.organizationName}
                  description={
                    contribuyente.fechaVinculacion
                      ? `Vinculado el ${formatInTimeZone(contribuyente.fechaVinculacion, 'DD/MM/YYYY')}`
                      : undefined
                  }
                />
                <Tag color="blue">{contribuyente.timbresPlanAportados} timbres/mes</Tag>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Activation Modal */}
      <Modal
        title="Activar Facturación Electrónica"
        open={showActivateModal}
        onOk={handleActivateFacturacion}
        onCancel={() => setShowActivateModal(false)}
        confirmLoading={activating}
        okText="Activar"
        cancelText="Cancelar"
        width={600}
      >
        <Alert
          message="Confirmación de Activación"
          description="Se registrará su restaurante en el sistema de facturación electrónica. Verifique que los datos fiscales sean correctos antes de continuar."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Text>
          <strong>RFC:</strong> {facturacionForm.getFieldValue('rfc')}
        </Text>
        <br />
        <Text>
          <strong>Razón Social:</strong> {facturacionForm.getFieldValue('razonSocial')}
        </Text>
        <br />
        <Text>
          <strong>Régimen Fiscal:</strong> {facturacionForm.getFieldValue('regimenFiscal')}
        </Text>
      </Modal>

      {/* Deactivation Modal */}
      <Modal
        title="¿Desactivar Facturación?"
        open={showDeactivateModal}
        onOk={confirmDeactivateFacturacion}
        onCancel={() => setShowDeactivateModal(false)}
        confirmLoading={deactivating}
        okText="Desactivar"
        okType="danger"
        cancelText="Cancelar"
        width={500}
      >
        <Alert
          message="Confirmación de Desactivación"
          description="Se desactivará la facturación electrónica. Podrá volver a activarla cuando lo necesite."
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
        />
      </Modal>
    </div>
  );
};

export default ConfiguracionFiscalTab;
